import { useMemo } from "react";
import PropTypes from "prop-types";
import Flx from "../../../components/layout/Flx";
import { useMediaQuery, useTheme } from "@mui/material";

/**
 * Responsive CSS-grid container (renders a wrapping `Flx`). Auto-fits columns
 * of `minmax(width, grow fr)` and collapses to a single column on mobile.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Grid items.
 * @param {string} [props.width="300px"] - Min column width for `auto-fit` minmax.
 * @param {number} [props.grow=1] - `fr` growth factor per column.
 * @param {number} [props.gap=1] - Gap as a MUI spacing multiple (×8px), not px.
 * @param {object} [props.sx] - Style overrides merged last.
 * @param {string} [props.className] - Class applied to the underlying Flx.
 * @param {boolean} [props.suppressAutoMobile=false] - If `true`, stay multi-column on mobile.
 * @param {number} [props.lastItemSpan=1] - Column span for the last child (>1 to span).
 * @returns {JSX.Element}
 */
const FlxGrid = ({
  children,
  width = "300px",
  grow = 1,
  sx,
  gap = 1,
  className,
  suppressAutoMobile = false,
  lastItemSpan = 1,
  ...rest
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const styles = useMemo(() => {
    const isSingleColumn = isMobile && !suppressAutoMobile;

    return {
      display: "grid",
      // `min(width, 100%)` rather than a bare `width`: a fixed minmax minimum
      // cannot shrink, so in any container narrower than `width` (a modal, a
      // sidebar, an AG Grid master-detail row) the track overflows its parent
      // and content spills out of the card. Identical behaviour whenever the
      // container is wide enough.
      gridTemplateColumns: isSingleColumn
        ? "1fr"
        : `repeat(auto-fit, minmax(min(${width}, 100%), ${grow}fr))`,
      gap: gap,

      // make last child span columns (only when not single column)
      ...(!isSingleColumn && lastItemSpan > 1
        ? {
            "& > *:last-child": {
              gridColumn: `span ${lastItemSpan}`,
            },
          }
        : {}),

      ...sx,
    };
  }, [sx, width, grow, gap, isMobile, suppressAutoMobile, lastItemSpan]);

  return (
    <Flx wrap sx={styles} className={className} {...rest}>
      {children}
    </Flx>
  );
};

FlxGrid.propTypes = {
  children: PropTypes.node,
  width: PropTypes.string,
  grow: PropTypes.number,
  gap: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
  className: PropTypes.string,
  suppressAutoMobile: PropTypes.bool,
  lastItemSpan: PropTypes.number,
};

export default FlxGrid;
